import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import api from '../services/api';

const DistrictUploadStatusReport = () => {
  const [reportData, setReportData] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState('');

  const [selectedDistrict, setSelectedDistrict] =
    useState('all');

  const [selectedYear, setSelectedYear] =
    useState('all');

  const [selectedScheme, setSelectedScheme] =
    useState('all');

  const [selectedMonth, setSelectedMonth] =
    useState('all');

  const [selectedStatus, setSelectedStatus] =
    useState('all');

  const [search, setSearch] =
    useState('');

  const normalizeYear = (value) => {
    const raw =
      String(value || '').trim();

    const digits =
      raw.replace(/\D/g, '');

    if (digits.length === 4) {
      return (
        `${digits.slice(0, 2)}-` +
        `${digits.slice(2, 4)}`
      );
    }

    return raw;
  };

  const getUploadUrl = (filePath) => {
    if (!filePath) return '';

    const cleanPath =
      String(filePath).trim();

    if (
      cleanPath.startsWith('http://') ||
      cleanPath.startsWith('https://')
    ) {
      return cleanPath;
    }

    const normalized =
      cleanPath.replace(/^\/+/, '');

    if (
      normalized.startsWith(
        'uploads/'
      )
    ) {
      return (
        `https://crd-training.in/` +
        normalized
      );
    }

    return (
      `https://crd-training.in/uploads/` +
      normalized
    );
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(
        `${api.BASE_URL}/project/report/district-upload-status`,
        {
          method: 'GET',

          headers: {
            'Content-Type':
              'application/json',

            Authorization:
              localStorage.getItem(
                'authToken'
              )
          }
        }
      );

      const result =
        await response.json();

      if (
        !response.ok ||
        result.error
      ) {
        throw new Error(
          result.message ||
          'Failed to load report'
        );
      }

      setReportData(
        result.data || []
      );

    } catch (err) {
      console.error(
        'Upload status report error:',
        err
      );

      setError(
        err.message ||
        'Failed to load report'
      );

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const districtOptions =
    useMemo(() => {
      const map = new Map();

      reportData.forEach(row => {
        const code =
          String(
            row.district_code || ''
          ).toUpperCase();

        if (!code) return;

        map.set(code, {
          code,
          name:
            row.district_name ||
            code
        });
      });

      return Array.from(
        map.values()
      ).sort(
        (a, b) =>
          a.name.localeCompare(
            b.name
          )
      );
    }, [reportData]);

  const yearOptions =
    useMemo(() => {
      return [
        ...new Set(
          reportData
            .map(row =>
              normalizeYear(
                row.year
              )
            )
            .filter(Boolean)
        )
      ].sort().reverse();
    }, [reportData]);

  const schemeOptions =
    useMemo(() => {
      return [
        ...new Set(
          reportData
            .map(row =>
              String(
                row.scheme || ''
              ).toUpperCase()
            )
            .filter(Boolean)
        )
      ].sort();
    }, [reportData]);

  const monthOptions =
    useMemo(() => {
      return [
        ...new Set(
          reportData
            .map(row =>
              String(
                row.month || ''
              )
            )
            .filter(Boolean)
        )
      ].sort(
        (a, b) =>
          Number(a) - Number(b)
      );
    }, [reportData]);

  const filteredData =
    useMemo(() => {
      const searchText =
        search
          .trim()
          .toLowerCase();

      return reportData.filter(
        row => {
          if (
            selectedDistrict !==
              'all' &&
            String(
              row.district_code ||
              ''
            ).toUpperCase() !==
              selectedDistrict
          ) {
            return false;
          }

          if (
            selectedYear !==
              'all' &&
            normalizeYear(
              row.year
            ) !== selectedYear
          ) {
            return false;
          }

          if (
            selectedScheme !==
              'all' &&
            String(
              row.scheme ||
              ''
            ).toUpperCase() !==
              selectedScheme
          ) {
            return false;
          }

          if (
            selectedMonth !==
              'all' &&
            String(
              row.month ||
              ''
            ) !== selectedMonth
          ) {
            return false;
          }

          if (
            selectedStatus !==
              'all' &&
            row.overall_status !==
              selectedStatus
          ) {
            return false;
          }

          if (searchText) {
            const searchValue = [
              row.district_name,
              row.district_code,
              row.training_code,
              row.title,
              row.scheme
            ]
              .join(' ')
              .toLowerCase();

            if (
              !searchValue.includes(
                searchText
              )
            ) {
              return false;
            }
          }

          return true;
        }
      );
    }, [
      reportData,
      selectedDistrict,
      selectedYear,
      selectedScheme,
      selectedMonth,
      selectedStatus,
      search
    ]);

  const summary =
    useMemo(() => {
      return {
        total:
          filteredData.length,

        completed:
          filteredData.filter(
            row =>
              row.overall_status ===
              'Completed'
          ).length,

        photoPending:
          filteredData.filter(
            row =>
              row.overall_status ===
              'Photo Pending'
          ).length,

        treasuryPending:
          filteredData.filter(
            row =>
              row.overall_status ===
              'Treasury Pending'
          ).length,

        notStarted:
          filteredData.filter(
            row =>
              row.overall_status ===
              'Not Started'
          ).length
      };
    }, [filteredData]);

  const clearFilters = () => {
    setSelectedDistrict('all');
    setSelectedYear('all');
    setSelectedScheme('all');
    setSelectedMonth('all');
    setSelectedStatus('all');
    setSearch('');
  };

  const downloadExcel = () => {
    if (
      filteredData.length === 0
    ) {
      alert(
        'No data available'
      );

      return;
    }

    const escapeCsv = value => {
      const text =
        String(value ?? '');

      return `"${text.replace(
        /"/g,
        '""'
      )}"`;
    };

    const rows = [
      [
        'S.No',
        'District',
        'Training Code',
        'Training Title',
        'Financial Year',
        'Month',
        'Scheme',
        'Photo Count',
        'Photo Status',
        'Treasury Bill Status',
        'Overall Status'
      ].join(',')
    ];

    filteredData.forEach(
      (row, index) => {
        rows.push(
          [
            index + 1,

            escapeCsv(
              row.district_name ||
              row.district_code
            ),

            escapeCsv(
              row.training_code
            ),

            escapeCsv(
              row.title
            ),

            escapeCsv(
              normalizeYear(
                row.year
              )
            ),

            escapeCsv(
              row.month
            ),

            escapeCsv(
              row.scheme
            ),

            row.photo_count || 0,

            escapeCsv(
              row.photo_count > 0
                ? 'Uploaded'
                : 'Pending'
            ),

            escapeCsv(
              row.treasury_uploaded
                ? 'Uploaded'
                : 'Pending'
            ),

            escapeCsv(
              row.overall_status
            )
          ].join(',')
        );
      }
    );

    const blob = new Blob(
      [
        '\uFEFF' +
        rows.join('\n')
      ],
      {
        type:
          'text/csv;charset=utf-8;'
      }
    );

    const url =
      URL.createObjectURL(blob);

    const link =
      document.createElement(
        'a'
      );

    link.href = url;

    link.download =
      `District_Upload_Status_${new Date()
        .toISOString()
        .split('T')[0]}.csv`;

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    URL.revokeObjectURL(url);
  };

  const statusClass = status => {
    if (status === 'Completed') {
      return (
        'bg-green-100 ' +
        'text-green-700'
      );
    }

    if (
      status === 'Not Started'
    ) {
      return (
        'bg-red-100 ' +
        'text-red-700'
      );
    }

    return (
      'bg-yellow-100 ' +
      'text-yellow-700'
    );
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">

      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-gray-800">
          District Upload Status Report
        </h1>

        <p className="text-sm text-gray-500 mt-1">
          Training photo and Treasury Bill submission status
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-5">

        <div className="bg-white border rounded-lg shadow-sm p-4">
          <p className="text-sm text-gray-500">
            Total Trainings
          </p>

          <p className="text-2xl font-bold text-gray-800">
            {summary.total}
          </p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-700">
            Completed
          </p>

          <p className="text-2xl font-bold text-green-700">
            {summary.completed}
          </p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-700">
            Photo Pending
          </p>

          <p className="text-2xl font-bold text-yellow-700">
            {summary.photoPending}
          </p>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <p className="text-sm text-orange-700">
            Treasury Pending
          </p>

          <p className="text-2xl font-bold text-orange-700">
            {summary.treasuryPending}
          </p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">
            Not Started
          </p>

          <p className="text-2xl font-bold text-red-700">
            {summary.notStarted}
          </p>
        </div>

      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 mb-4">

        <button
          onClick={fetchReport}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          {loading
            ? 'Refreshing...'
            : 'Refresh'}
        </button>

        <button
          onClick={clearFilters}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
        >
          Clear Filters
        </button>

        <button
          onClick={downloadExcel}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          Download Excel
        </button>

      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg shadow-sm p-4 mb-5">

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4">

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              District
            </label>

            <select
              value={selectedDistrict}
              onChange={e =>
                setSelectedDistrict(
                  e.target.value
                )
              }
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              <option value="all">
                All Districts
              </option>

              {districtOptions.map(
                district => (
                  <option
                    key={
                      district.code
                    }
                    value={
                      district.code
                    }
                  >
                    {district.name}
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Financial Year
            </label>

            <select
              value={selectedYear}
              onChange={e =>
                setSelectedYear(
                  e.target.value
                )
              }
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              <option value="all">
                All Years
              </option>

              {yearOptions.map(
                year => (
                  <option
                    key={year}
                    value={year}
                  >
                    {year}
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Scheme
            </label>

            <select
              value={selectedScheme}
              onChange={e =>
                setSelectedScheme(
                  e.target.value
                )
              }
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              <option value="all">
                All Schemes
              </option>

              {schemeOptions.map(
                scheme => (
                  <option
                    key={scheme}
                    value={scheme}
                  >
                    {scheme}
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Month
            </label>

            <select
              value={selectedMonth}
              onChange={e =>
                setSelectedMonth(
                  e.target.value
                )
              }
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              <option value="all">
                All Months
              </option>

              {monthOptions.map(
                month => (
                  <option
                    key={month}
                    value={month}
                  >
                    {month}
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Status
            </label>

            <select
              value={selectedStatus}
              onChange={e =>
                setSelectedStatus(
                  e.target.value
                )
              }
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              <option value="all">
                All Status
              </option>

              <option value="Completed">
                Completed
              </option>

              <option value="Photo Pending">
                Photo Pending
              </option>

              <option value="Treasury Pending">
                Treasury Pending
              </option>

              <option value="Not Started">
                Not Started
              </option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Search
            </label>

            <input
              type="text"
              value={search}
              onChange={e =>
                setSearch(
                  e.target.value
                )
              }
              placeholder="Code / District..."
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
          </div>

        </div>

        <div className="mt-3 text-sm text-gray-500">
          Showing{' '}
          <strong>
            {filteredData.length}
          </strong>
          {' '}of{' '}
          <strong>
            {reportData.length}
          </strong>
          {' '}records
        </div>

      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">

        <div className="bg-blue-100 px-4 py-3">
          <h2 className="text-lg font-semibold text-center text-gray-800">
            District Upload Status
          </h2>
        </div>

        <div className="overflow-x-auto">

          <table className="w-full border-collapse">

            <thead className="bg-gray-100">
              <tr>

                <th className="border px-3 py-3 text-sm">
                  S.No
                </th>

                <th className="border px-3 py-3 text-sm">
                  District
                </th>

                <th className="border px-3 py-3 text-sm">
                  Training Code
                </th>

                <th className="border px-3 py-3 text-sm">
                  Training Title
                </th>

                <th className="border px-3 py-3 text-sm">
                  Photos
                </th>

                <th className="border px-3 py-3 text-sm">
                  Treasury Bill
                </th>

                <th className="border px-3 py-3 text-sm">
                  Overall Status
                </th>

              </tr>
            </thead>

            <tbody>

              {filteredData.length > 0
                ? filteredData.map(
                    (row, index) => (

                    <tr
                      key={row.id}
                      className="hover:bg-gray-50"
                    >

                      <td className="border px-3 py-3 text-sm text-center">
                        {index + 1}
                      </td>

                      <td className="border px-3 py-3 text-sm">
                        <strong>
                          {row.district_name}
                        </strong>

                        <div className="text-xs text-gray-400">
                          {row.district_code}
                        </div>
                      </td>

                      <td className="border px-3 py-3 text-sm">
                        {row.training_code}
                      </td>

                      <td className="border px-3 py-3 text-sm">
                        {row.title || '-'}
                      </td>

                      <td className="border px-3 py-3 text-sm text-center">

                        {row.photo_count > 0 ? (
                          <>
                            <div className="text-green-600 font-medium mb-1">
                              {row.photo_count} Uploaded
                            </div>

                            <div className="space-x-2">

                              {(row.training_photos || [])
                                .map(
                                  (
                                    photo,
                                    photoIndex
                                  ) => (

                                  <a
                                    key={
                                      photoIndex
                                    }
                                    href={
                                      getUploadUrl(
                                        photo
                                      )
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 underline text-xs"
                                  >
                                    View {photoIndex + 1}
                                  </a>

                                )
                              )}

                            </div>
                          </>
                        ) : (
                          <span className="text-red-600">
                            Pending
                          </span>
                        )}

                      </td>

                      <td className="border px-3 py-3 text-sm text-center">

                        {row.treasury_uploaded ? (

                          <a
                            href={
                              getUploadUrl(
                                row.ppa_doc?.[0]
                              )
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline"
                          >
                            View Document
                          </a>

                        ) : (

                          <span className="text-red-600">
                            Pending
                          </span>

                        )}

                      </td>

                      <td className="border px-3 py-3 text-sm text-center">

                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${statusClass(
                            row.overall_status
                          )}`}
                        >
                          {row.overall_status}
                        </span>

                      </td>

                    </tr>
                  )
                )
                : (

                  <tr>
                    <td
                      colSpan="7"
                      className="border px-3 py-8 text-center text-gray-500"
                    >
                      {loading
                        ? 'Loading...'
                        : 'No records found'}
                    </td>
                  </tr>

                )}

            </tbody>

          </table>

        </div>
      </div>

    </div>
  );
};

export default DistrictUploadStatusReport;